/* eslint-disable import/no-duplicates */
import React, { useState } from 'react';
import { format, isToday, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Chart } from 'react-google-charts';
import { useCurrentEffect } from '../../../../../../../../lib/use-current-effect';

import {
  getMeasurementsByDateRange,
  getLastMeasurementsInfo,
} from '../../../../../../../../services/metrics';
import { usePatient } from '../../../../../../../../hooks/patient';

import Img from '../../../../../../../../assets/images/sono.svg';

import Default, { ShowScreenType } from '../_Default';
import { ChartsProps } from '../../MetricView';
import { DetailsChart } from '../../MetricView/styles';
import { MetricTypeEnum } from '../../../../../../../../enums/MetricTypeEnum';

const SleepChart: React.FC<ChartsProps> = ({ dateRange }) => {
  const { patient } = usePatient();

  const [dataOneDay, setDataOneDay] = useState<string | number>();
  const [showScreen, setShowScreen] = useState<ShowScreenType>('loading');
  const [graphData, setGraphData] = useState<
    (string | number | { v: number; f: string })[][]
  >([['00:00', 0]]);

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          if (isCurrent()) {
            setShowScreen('loading');
          }

          const { startDate, endDate } = dateRange;

          if (startDate === endDate) {
            getLastMeasurementsInfo({
              patientId: patient.id,
              metrics: [MetricTypeEnum.SLEEP],
            })
              .then((response) => {
                const checkMeasurementToday = response.find(
                  (item) =>
                    item.metricType === MetricTypeEnum.SLEEP &&
                    isToday(new Date(item.mensuramentDate)),
                );

                if (isCurrent()) {
                  if (checkMeasurementToday) {
                    setShowScreen('chart');
                    setDataOneDay(checkMeasurementToday.sleepTotalTimeParsed);
                  } else {
                    setShowScreen('noData');
                  }
                }
              })
              .catch(() => {
                if (isCurrent()) {
                  setShowScreen('noData');
                }
              });
          } else {
            getMeasurementsByDateRange({
              patientId: patient.id,
              metrics: MetricTypeEnum.SLEEP,
              startDate,
              endDate,
            })
              .then((response) => {
                if (isCurrent()) {
                  if (response.length > 0) {
                    const result = response.map(
                      ({
                        sleepTotalTime: sleepTimeSeconds,
                        mensuramentDate,
                        sleepTotalTimeParsed,
                      }) => [
                        format(new Date(mensuramentDate), "dd'/'MM'/'yyyy"),
                        { v: sleepTimeSeconds / 3600, f: sleepTotalTimeParsed },
                      ],
                    );

                    if (result.length === 1) {
                      const date = new Date(response[0].mensuramentDate);
                      const dayOne = format(
                        new Date(subDays(date, 1)),
                        "dd'/'MM'/'yyyy",
                      );
                      const dayTwo = format(
                        new Date(subDays(date, 2)),
                        "dd'/'MM'/'yyyy",
                      );

                      result.unshift([dayOne, { v: 0, f: '' }]);
                      result.unshift([dayTwo, { v: 0, f: '' }]);
                    }

                    setShowScreen('chart');
                    setGraphData(result);
                  } else {
                    setShowScreen('noData');
                  }
                }
              })
              .catch(() => {
                if (isCurrent()) {
                  setShowScreen('noData');
                }
              });
          }
        }
      })();
    },
    [dateRange, patient],
  );

  return (
    <Default showScreen={showScreen}>
      {dateRange.startDate === dateRange.endDate ? (
        <DetailsChart>
          <h2>
            {`Hoje - ${format(dateRange.startDate, "dd'/'MM", {
              locale: ptBR,
            })}`}
          </h2>

          <div>
            <img src={Img} alt="icone de sono" />

            <div>
              <span>Hoje o paciente dormiu</span>

              <h1>{dataOneDay}</h1>
            </div>
          </div>
        </DetailsChart>
      ) : (
        <Chart
          width="100%"
          height="300px"
          chartType="Bar"
          options={{
            legend: { position: 'none' },
            colors: ['#56CCF2'],
            vAxis: {
              ticks: [0, 2, 4, 6, 8, 10, 12, 14, 16],
            },
          }}
          data={[['', 'Sono'], ...graphData]}
          rootProps={{ 'data-testid': '4' }}
        />
      )}
    </Default>
  );
};

export default SleepChart;
